import React, { useState } from 'react';
import { StaffRole, StaffUser } from '../types';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Lock,
  KeyRound,
  X,
  AlertCircle,
  Mail,
  Eye,
  EyeOff
} from 'lucide-react';
import { playModernSuccessChime } from '../utils/audio';

interface StaffLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetRole?: StaffRole;
}

export const StaffLoginModal: React.FC<StaffLoginModalProps> = ({ isOpen, onClose }) => {
  const { loginStaff, authenticateStaffUser } = useApp();
  const { t } = useLanguage();

  const [authMode, setAuthMode] = useState<'pin' | 'password'>('pin');
  const [pin, setPin] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handlePinDigit = (digit: string) => {
    if (pin.length < 4) {
      const nextPin = pin + digit;
      setPin(nextPin);
      setErrorMsg('');
      if (nextPin.length === 4) {
        verifyPin(nextPin);
      }
    }
  };

  const handlePinBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setErrorMsg('');
  };

  const handlePinClear = () => {
    setPin('');
    setErrorMsg('');
  };

  const verifyPin = async (pinToTest: string) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const res = await authenticateStaffUser({
        login_type: 'pin',
        pin_code: pinToTest
      });

      if (res.success && res.user) {
        playModernSuccessChime();
        loginStaff(res.user.role as StaffRole, res.user);
        onClose();
        setPin('');
      } else {
        setErrorMsg(res.error || 'Invalid PIN code. Please try again.');
        setPin('');
      }
    } catch (e: any) {
      setErrorMsg('Login failed: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setErrorMsg('Please enter email/mobile and password');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    try {
      const res = await authenticateStaffUser({
        login_type: 'password',
        identifier: identifier.trim(),
        password: password
      });

      if (res.success && res.user) {
        playModernSuccessChime();
        loginStaff(res.user.role as StaffRole, res.user);
        onClose();
        setPassword('');
        setIdentifier('');
      } else {
        setErrorMsg(res.error || 'Invalid email or password.');
      }
    } catch (e: any) {
      setErrorMsg('Login failed: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 120 }}>
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '420px', width: '92%', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border-subtle)',
            padding: '16px 20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(224, 90, 54, 0.15)',
                color: 'var(--accent-copper)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Lock size={20} />
            </div>
            <div>
              <div className="modal-title" style={{ fontSize: '1.05rem', fontWeight: 800 }}>
                Staff & Admin Portal
              </div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                Henkilökunnan & Pääkäyttäjän Kirjautuminen
              </div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Tab Switcher: PIN vs Password */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            background: 'var(--bg-secondary)',
            padding: '6px',
            borderBottom: '1px solid var(--border-subtle)'
          }}
        >
          <button
            type="button"
            onClick={() => { setAuthMode('pin'); setErrorMsg(''); }}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: authMode === 'pin' ? 'var(--bg-card)' : 'transparent',
              color: authMode === 'pin' ? 'var(--accent-copper)' : 'var(--text-muted)',
              fontWeight: 800,
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              boxShadow: authMode === 'pin' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <KeyRound size={15} />
            <span>Quick PIN Login</span>
          </button>

          <button
            type="button"
            onClick={() => { setAuthMode('password'); setErrorMsg(''); }}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: authMode === 'password' ? 'var(--bg-card)' : 'transparent',
              color: authMode === 'password' ? 'var(--accent-copper)' : 'var(--text-muted)',
              fontWeight: 800,
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              boxShadow: authMode === 'password' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <Mail size={15} />
            <span>Email / Password</span>
          </button>
        </div>

        {/* Error Alert */}
        {errorMsg && (
          <div
            style={{
              margin: '16px 20px 0 20px',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(244, 63, 94, 0.12)',
              border: '1px solid var(--accent-rose)',
              color: 'var(--accent-rose)',
              fontSize: '0.78rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="modal-body" style={{ padding: '20px' }}>
          {/* TAB 1: QUICK PIN LOGIN */}
          {authMode === 'pin' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                Syötä 4-numeroinen PIN-koodi / Enter 4-digit Staff PIN
              </div>

              {/* PIN Bubbles Display */}
              <div style={{ display: 'flex', gap: '12px', margin: '4px 0' }}>
                {[0, 1, 2, 3].map(idx => (
                  <div
                    key={idx}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${pin.length > idx ? 'var(--accent-copper)' : 'var(--border-medium)'}`,
                      background: pin.length > idx ? 'var(--accent-copper)' : 'transparent',
                      transition: 'all 0.15s ease'
                    }}
                  />
                ))}
              </div>

              {/* Numeric Keypad */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', width: '100%', maxWidth: '270px' }}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'].map(btn => {
                  const isAction = btn === 'C' || btn === '⌫';
                  return (
                    <button
                      type="button"
                      key={btn}
                      disabled={isLoading}
                      onClick={() => {
                        if (btn === 'C') handlePinClear();
                        else if (btn === '⌫') handlePinBackspace();
                        else handlePinDigit(btn);
                      }}
                      style={{
                        padding: '16px 0',
                        fontSize: isAction ? '0.95rem' : '1.3rem',
                        fontWeight: 800,
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--border-subtle)',
                        background: isAction ? 'var(--bg-secondary)' : 'var(--bg-surface)',
                        color: isAction ? 'var(--accent-copper)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.1s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.94)')}
                      onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      {btn}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: EMAIL / PASSWORD LOGIN */}
          {authMode === 'password' && (
            <form onSubmit={handlePasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label">Email or Mobile Number</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder="Enter email or mobile..."
                    style={{ paddingLeft: '36px' }}
                  />
                  <Mail
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)'
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password / Salasana</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="form-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter password..."
                    style={{ paddingLeft: '36px', paddingRight: '38px' }}
                  />
                  <Lock
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading}
                style={{ width: '100%', padding: '12px', fontSize: '0.92rem', marginTop: '6px' }}
              >
                {isLoading ? 'Verifying...' : 'Sign In to Portal'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffLoginModal;
